import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';
import api from '../services/api';
import GroupIcon from '@mui/icons-material/Group';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import FeedIcon from '@mui/icons-material/Feed';
import EventIcon from '@mui/icons-material/Event';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HomeIcon from '@mui/icons-material/Home';
import UpgradeIcon from '@mui/icons-material/Upgrade';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();
  const { families, currentFamily, selectFamily } = useFamily();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isAppAdmin, setIsAppAdmin] = useState(false);

  // Check if user is app admin
  React.useEffect(() => {
    const checkAppAdmin = async () => {
      if (!user) {
        setIsAppAdmin(false);
        return;
      }
      try {
        await api.get('/families/admin/all/');
        setIsAppAdmin(true);
      } catch (error) {
        setIsAppAdmin(false);
      }
    };
    checkAppAdmin();
  }, [user]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
      setDrawerOpen(false);
    } catch (error) {
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  if (location.pathname === '/login') {
    return null;
  }

  const menuItems = [
    { text: 'Home', icon: <HomeIcon />, path: '/' },
    { text: 'Events', icon: <EventIcon />, path: '/events' },
    { text: 'Wishlists', icon: <CardGiftcardIcon />, path: '/wishlists' },
    { text: 'Name Picker', icon: <ShuffleIcon />, path: '/name-picker' },
    { text: 'Recipes', icon: <RestaurantIcon />, path: '/recipes' },
    { text: 'Gift Tracker', icon: <TrackChangesIcon />, path: '/gift-tracker' },
    { text: 'Budget', icon: <AccountBalanceWalletIcon />, path: '/budget-tracker' },
    { text: 'Calendar', icon: <CalendarTodayIcon />, path: '/calendar' },
  ];

  const drawer = (
    <Box sx={{ width: 280 }} role="presentation">
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Menu</Typography>
        <IconButton onClick={() => setDrawerOpen(false)}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      {user && (
        <>
          {families.length > 0 && (
            <>
              <Box sx={{ p: 2 }}>
                <FormControl fullWidth size="small">
                  <Select
                    value={currentFamily?.id || ''}
                    onChange={(e) => {
                      const family = families.find(f => f.id === e.target.value);
                      if (family) selectFamily(family);
                    }}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return <em>Select Family</em>;
                      }
                      const family = families.find(f => f.id === selected);
                      return family ? family.name : <em>Select Family</em>;
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select a Family</em>
                    </MenuItem>
                    {families.map((family) => (
                      <MenuItem key={family.id} value={family.id}>
                        {family.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Divider />
              <List>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleNavigate('/families')}>
                    <ListItemIcon><GroupIcon /></ListItemIcon>
                    <ListItemText primary="Families" />
                  </ListItemButton>
                </ListItem>
                {currentFamily && (
                  <>
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => handleNavigate(`/families/${currentFamily.id}/feed`)}>
                        <ListItemIcon><FeedIcon /></ListItemIcon>
                        <ListItemText primary="Family Feed" />
                      </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => handleNavigate(`/families/${currentFamily.id}/dashboard`)}>
                        <ListItemIcon><AdminPanelSettingsIcon /></ListItemIcon>
                        <ListItemText primary="Dashboard" />
                      </ListItemButton>
                    </ListItem>
                  </>
                )}
              </List>
              <Divider />
            </>
          )}
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleNavigate('/subscription')}>
                <ListItemIcon><UpgradeIcon /></ListItemIcon>
                <ListItemText primary="Subscription" />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
          {isAppAdmin && (
            <>
              <List>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleNavigate('/app-admin')}>
                    <ListItemIcon><AdminPanelSettingsIcon /></ListItemIcon>
                    <ListItemText primary="App Administration" />
                  </ListItemButton>
                </ListItem>
              </List>
              <Divider />
            </>
          )}
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton onClick={() => handleNavigate(item.path)}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Divider />
          <List>
            <ListItem>
              <ListItemText primary={user.name} secondary="Logged in" />
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          </List>
        </>
      )}
    </Box>
  );
  
  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: theme.palette.primary.main }}>
        <Toolbar>
          <Box
            component="div"
            sx={{ 
              flexGrow: { xs: 0, sm: 1 }, 
              cursor: 'pointer', 
              mr: { xs: 1, sm: 0 },
              display: 'flex',
              alignItems: 'center',
            }}
            onClick={() => navigate('/')}
          >
            <img 
              src="/Logo.png"
              alt="Jubilea" 
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
              style={{ 
                height: '32px',
                maxWidth: '150px',
                objectFit: 'contain'
              }}
            />
            <Typography
              variant="h6"
              component="span"
              sx={{ display: 'none' }}
            >
              Jubilea
            </Typography>
          </Box>
          {user && (
            <>
              {isMobile ? (
                <IconButton
                  color="inherit"
                  edge="end"
                  onClick={() => setDrawerOpen(true)}
                  sx={{ ml: 'auto' }}
                >
                  <MenuIcon />
                </IconButton>
              ) : (
                <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1, md: 2 }, alignItems: 'center', flexWrap: 'wrap' }}>
                  {families.length > 0 && (
                    <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 180 }, mr: 1 }}>
                      <Select
                        value={currentFamily?.id || ''}
                        onChange={(e) => {
                          const family = families.find(f => f.id === e.target.value);
                          if (family) selectFamily(family);
                        }}
                        displayEmpty
                        sx={{ color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' } }}
                        renderValue={(selected) => {
                          if (!selected) {
                            return <em>Select Family</em>;
                          }
                          const family = families.find(f => f.id === selected);
                          return family ? family.name : <em>Select Family</em>;
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>Select a Family</em>
                        </MenuItem>
                        {families.map((family) => (
                          <MenuItem key={family.id} value={family.id}>
                            {family.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <Tooltip title="Families">
                    <IconButton
                      color="inherit"
                      onClick={() => navigate('/families')}
                      size="small"
                    >
                      <GroupIcon />
                    </IconButton>
                  </Tooltip>
                  {currentFamily && (
                    <>
                      <Tooltip title="Family Feed">
                        <IconButton
                          color="inherit"
                          onClick={() => navigate(`/families/${currentFamily.id}/feed`)}
                          size="small"
                        >
                          <FeedIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Family Dashboard">
                        <IconButton
                          color="inherit"
                          onClick={() => navigate(`/families/${currentFamily.id}/dashboard`)}
                          size="small"
                        >
                          <AdminPanelSettingsIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {isAppAdmin && (
                    <Tooltip title="App Administration">
                      <IconButton
                        color="inherit"
                        onClick={() => navigate('/app-admin')}
                        size="small"
                      >
                        <AdminPanelSettingsIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Button color="inherit" onClick={() => navigate('/')} size="small" startIcon={<HomeIcon />}>
                    Home
                  </Button>
                  <Button color="inherit" onClick={() => navigate('/events')} size="small">
                    Events
                  </Button>
                  <Button color="inherit" onClick={() => navigate('/wishlists')} size="small">
                    Wishlists
                  </Button>
                  <Button color="inherit" onClick={() => navigate('/name-picker')} size="small">
                    Name Picker
                  </Button>
                  <Button color="inherit" onClick={() => navigate('/recipes')} size="small">
                    Recipes
                  </Button>
                  <Button color="inherit" onClick={() => navigate('/gift-tracker')} size="small">
                    Gift Tracker
                  </Button>
                  <Button color="inherit" onClick={() => navigate('/budget-tracker')} size="small">
                    Budget
                  </Button>
                  <Button color="inherit" onClick={() => navigate('/calendar')} size="small">
                    Calendar
                  </Button>
                  <Typography variant="body2" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
                    {user.name}
                  </Typography>
                  <Button color="inherit" onClick={handleLogout} size="small">
                    Logout
                  </Button>
                </Box>
              )}
            </>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;
